import styled from "styled-components/macro";
import Stack from "../Stack";
import { Box, BoxContent, BoxHeader, BoxTitle } from "../Box";
import Input, { Label } from "../Input";
import Checkbox from "../Checkbox";
import MessageTemplates from "./MessageTemplates";
import { CAMPAIGN_TYPES } from "./constants";
import ContactsWell from "../ContactsWell";
import { WarningAlert } from "../Alert";
import { OnChange } from "../../hooks/use-optional-state";
import { CampaignResponse } from "../../api/campaigns";
import Keywords from "./Keywords";
import { DataWithErrors } from "../../api";
import { MediaMimeType } from "./types";
import { useFields } from "../../hooks/use-fields";
import { ObjectWithoutIds } from "../CampaignSelector";

export type EditableCampaignKeyword = DataWithErrors<{
  id?: string;
  _destroy?: boolean;
  keyword: string;
}>;

export type EditableMessageTemplate = DataWithErrors<
  Pick<
    CampaignResponse["messageTemplates"][number],
    | "name"
    | "body"
    | "messageTemplateType"
    | "deliveryStrategy"
    | "delayInMinutes"
    | "delayInDays"
    | "daysBeforeEvent"
    | "daysAfterEvent"
    | "minutesAfterEvent"
    | "minutesBeforeEvent"
    | "position"
  > & {
    id?: string;
    tempId?: number;
    _destroy?: boolean;
    deliverAt?: Date | null;
    imageUrl?: string | null;
    imageType?: MediaMimeType | null;
    voicemailId?: string | null;
    messageTemplateRules: EditableMessageTemplateRule[];
  }
>;

export type EditableMessageTemplateRule = DataWithErrors<
  Pick<
    CampaignResponse["messageTemplates"][number]["messageTemplateRules"][number],
    "signal" | "shouldStopCampaign" | "expression"
  > & {
    id?: string;
    tempId?: number;
    contactGroupId?: string | null;
    campaignId?: string | null;
    _destroy?: boolean;
  }
>;

export type EditableCampaign = DataWithErrors<
  Pick<
    CampaignResponse,
    | "name"
    | "description"
    | "campaignType"
    | "removeContactsOnAnyInboundSmsMessage"
    | "removeContactsOnInboundPhoneCall"
    | "contactIds"
    | "contactFormOnKeyword"
    | "isCustom"
  > & {
    campaignKeywords: EditableCampaignKeyword[];
  } & {
    messageTemplates: EditableMessageTemplate[];
  } & {
    id?: string;
  }
>;

export function transformCampaignResponse(
  campaign: CampaignResponse | ObjectWithoutIds<CampaignResponse>
): EditableCampaign {
  return {
    ...campaign,
    contactIds: "contactIds" in campaign ? campaign.contactIds : [],
    messageTemplates: campaign.messageTemplates.map((template) => {
      const { media, deliverAtTime, ...value } = template;
      const transformedTemplate = {
        ...value,
        deliverAt: deliverAtTime ? new Date(deliverAtTime) : null,
        imageUrl: media && media.length ? media[0].url : null,
        imageType: media && media.length ? media[0].type : null,
        errors: {
          ...value.errors,
          ...(value.errors?.deliverAtTime
            ? { deliverAt: value.errors.deliverAtTime }
            : {}),
        },
      };

      return transformedTemplate;
    }),
  };
}

type Props = {
  value: EditableCampaign;
  onChange: OnChange<EditableCampaign>;
  errors: Record<string, string | string[]>;
};

export default function CampaignEditor({ value, onChange, errors }: Props) {
  const { field } = useFields({ value, onChange });

  return (
    <Container>
      <Stack space={10}>
        {!!errors && Object.keys(errors).length > 0 && (
          <Box error shaded>
            <BoxContent>
              There were errors while saving this campaign.
            </BoxContent>
          </Box>
        )}
        <Box elevated>
          <BoxHeader>
            <BoxTitle>
              {CAMPAIGN_TYPES[value.campaignType].text} Campaign
            </BoxTitle>
          </BoxHeader>
          <BoxContent>
            <Stack>
              {errors?.base && <WarningAlert>{errors.base}</WarningAlert>}
              <Input
                label="Campaign Name"
                {...field("name")}
                error={errors?.name}
                required
              />
              <Input
                type="textarea"
                label="Campaign Description"
                {...field("description")}
                error={errors?.description}
              />
              {value.campaignType === "drip" && (
                <Stack space={1}>
                  <Checkbox {...field("removeContactsOnAnyInboundSmsMessage")}>
                    Remove contact from campaign if they send you a message.
                  </Checkbox>
                  <Checkbox {...field("removeContactsOnInboundPhoneCall")}>
                    Remove contact from campaign if they call your Skipio
                    Number.
                  </Checkbox>
                </Stack>
              )}
              {/* Don't allow adding contacts until campaign is persisted. */}
              {Boolean(value.id) && (
                <>
                  <Label>Contacts Currently in Campaign</Label>
                  <ContactsWell
                    placeholder="There are no contacts in this campaign"
                    value={{
                      contactIds: value.contactIds,
                      groupIds: [],
                      keywordIds: [],
                    }}
                    onChange={(input) => {
                      if (typeof input === "function")
                        input = input({
                          contactIds: value.contactIds,
                          groupIds: [],
                          keywordIds: [],
                        });
                      const { contactIds } = input;
                      return field("contactIds").onChange(contactIds);
                    }}
                    canAdd={value.campaignType === "drip"}
                    canRemove={value.campaignType === "drip"}
                  />
                </>
              )}
              <>
                <Label>Keywords</Label>
                {value.campaignType === "drip" && (
                  <Keywords {...field("campaignKeywords")} />
                )}
                <Checkbox {...field("contactFormOnKeyword")}>
                  Send contact form to new contacts
                </Checkbox>
              </>
            </Stack>
          </BoxContent>
        </Box>
        <MessageTemplates
          campaignType={value.campaignType}
          {...field("messageTemplates")}
        />
      </Stack>
    </Container>
  );
}

const Container = styled.div`
  text-align: center;
  margin: auto;
  max-width: 550px;
`;
